import axios from 'axios';
import BigInt from 'json-bigint';

import { accessTokenStorage } from '@storage/authStorage';

export const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://dev-gms-api.innoforest.co.kr';

axios.interceptors.request.use(
  (config: any) => {
    const token = accessTokenStorage.get();
    if (token) {
      config.headers['authorization'] = `${token}`;
    }
    // FormData인 경우 Content-Type을 설정하지 않음 (브라우저가 자동으로 multipart/form-data와 boundary 설정)
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
      config.headers['Accept'] = 'application/json';
    }
    // config.transformResponse = function (response: any) {
    //   return BigInt().parse(response);
    // };
    config.baseURL = BASE_URL;
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);
/** 신규사업 등록요청 양식 다운르도  */
export const instance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

instance.interceptors.request.use(
  (config: any) => {
    const token = accessTokenStorage.get();
    if (token) {
      config.headers['authorization'] = `${token}`;
    }
    config.baseURL = BASE_URL;
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// axios.interceptors.response.use(
//   function (response) {
//     return response;
//   },
//   function (error) {
//     const { status, data } = error?.response || undefined;
//     if (status === 401 || status === 403) {
//       console.log('STATUS : ', status);
//       console.log('DATA : ', data);
//     }
//   }
// );

export const getList = async (resource: string, params: any) => {
  try {
    const response = await axios.get(resource, {
      params,
      transformResponse: function (response: any) {
        return BigInt().parse(response);
      },
    });
    return response.data;
  } catch (err: any) {
    // return { isError: true, data: err.response?.data };
    return Promise.reject(err);
  }
};
export const getOne = async (resource: string, id?: string) => {
  try {
    const response = await axios.get(`${resource}/${id}`, {
      transformResponse: function (response: any) {
        return BigInt().parse(response);
      },
    });
    return response.data;
  } catch (err: any) {
    // return { isError: true, data: err.response?.data };
    return Promise.reject(err);
  }
};

export const create = async (resource: string, payload: any) => {
  try {
    const response = await axios.post(resource, { ...payload });
    return response.data;
  } catch (err: any) {
    return { isError: true, data: err.response?.data };
  }
};

export const createWithFormData = async (
  resource: string,
  formData: FormData
) => {
  try {
    // FormData일 때는 Content-Type을 설정하지 않음 (브라우저가 자동으로 boundary 포함해서 설정)
    const response = await axios.post(resource, formData, {
      headers: {
        Accept: 'application/json',
      },
    });
    return response.data;
  } catch (err: any) {
    return { isError: true, data: err.response?.data };
  }
};

export const createThrowError = async (resource: string, payload: any) => {
  try {
    const response = await axios.post(resource, { ...payload });
    return response.data;
  } catch (err: any) {
    return Promise.reject(err);
  }
};

export const update = async (resource: string, payload?: any) => {
  try {
    const response = await axios.put(resource, { ...payload });
    return response.data;
  } catch (err: any) {
    return { isError: true, data: err.response?.data };
  }
};

export const updateThrowError = async (resource: string, payload?: any) => {
  try {
    const response = await axios.put(resource, { ...payload });
    return response.data;
  } catch (err: any) {
    return Promise.reject(err);
  }
};

export const remove = async (resource: string, payload?: any) => {
  try {
    const response = await axios.delete(resource, { ...payload });
    return response.data;
  } catch (err: any) {
    return { isError: true, data: err.response?.data };
  }
};

export const removeThrowError = async (resource: string, payload?: any) => {
  try {
    const response = await axios.delete(resource, { ...payload });
    return response.data;
  } catch (err: any) {
    return Promise.reject();
  }
};

/** 파일 다운로드 (범용 함수) */
export const fileDownload = async (
  resource: string,
  params?: any,
  onProgress?: (progress: number) => void,
  fileName?: string
) => {
  try {
    const { data, headers } = await instance.get(resource, {
      responseType: 'blob',
      params,
      onDownloadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });

    const extractDownloadFilename = (headers: any) => {
      // 파일명이 인자로 전달되었으면 우선 사용
      if (fileName) {
        return fileName;
      }

      const disposition = headers['content-disposition'];
      if (!disposition) {
        // content-disposition 헤더가 없으면 기본 파일명 사용
        return 'download';
      }

      const extractedFileName = decodeURI(
        disposition
          .match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]
          ?.replace(/['"]/g, '')
          .replace('UTF-8', '') || 'download'
      );

      return extractedFileName;
    };

    const blob = new Blob([data], {
      type: headers['content-type'] || 'application/octet-stream',
    });

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = extractDownloadFilename(headers);
    link.click();
    URL.revokeObjectURL(blobUrl);
  } catch (err: any) {
    // return { isError: true, data: err.response?.data };
    return Promise.reject(err);
  }
};

/** 엑셀 파일 다운로드 (기존 호환성을 위해 유지) - 필요시 여기서 로직 수정가능! */
export const excelDownload = async (
  resource: string,
  params?: any,
  fileName?: string
) => {
  return fileDownload(resource, params, undefined, fileName);
};

export const getErrorMessage = (status: number) => {
  switch (status) {
    case 401:
    case 403:
      return {
        title: '토근이 만료되었습니다.',
        content: '다시 로그인을 해주세요.',
      };
    case 409:
    case 500:
    default:
      return {
        title: '잠시 후 다시 시도해주세요.',
        content: '요청사항을 처리하는데 실패했습니다.',
      };
  }
};
